import { useMutation } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

export type EmailDocumentType = 'MEETING_NOTE' | 'MEETING_AGENDA' | 'DATA_EXPORT';

export type EmailContentResponse = {
  id: string;
  documentId: string;
  body: string;
  subject: string;
  recipientEmail: string;
};

type StartEmailContentParams = {
  clientId?: string;
  familyId?: string;
  documentId: string;
  documentType: EmailDocumentType;
};

export const useStartEmailContent = () => {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: ({ clientId, familyId, documentId, documentType }: StartEmailContentParams) => {
      const scope = familyId ? `families/${familyId}` : `clients/${clientId}`;
      return api.post<EmailContentResponse>(
        `${scope}/documents/${documentId}/email-content?document_type=${documentType}`,
        undefined,
        { timeoutMs: 60000 },
      );
    },
  });

  const startEmailContent = async (params: StartEmailContentParams) => {
    const { data } = await mutateAsync(params);
    return data;
  };

  return { startEmailContent, isGenerating: isLoading };
};

export const useDeleteEmailContent = () => {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: (emailContentId: string) => api.delete(`email-content/${emailContentId}`),
  });

  const deleteEmailContent = async (emailContentId: string) => {
    await mutateAsync(emailContentId);
  };

  return { deleteEmailContent, isDeleting: isLoading };
};

type UpdateEmailContentParams = {
  emailContentId: string;
  body: string;
  subject: string;
  recipientEmail: string;
};

export const useUpdateEmailContent = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ emailContentId, ...fields }: UpdateEmailContentParams) =>
      api.patch<EmailContentResponse>(`email-content/${emailContentId}`, fields),
  });

  const updateEmailContent = async (params: UpdateEmailContentParams) => {
    const { data } = await mutateAsync(params);
    return data;
  };

  return { updateEmailContent, isSaving: isPending };
};
